/**
 * A new ticket, with no AI (TMMAPP-0142 U2 item 13): its type (support case or feature request), a
 * title (at most 120) and a description (at most 4000) → `POST /v1/me/tickets`. Free for everyone.
 * The day's limit (`429`) reads "You've filed the most tickets allowed for today…". Guarded like
 * every editor. A locked member files a support case only (M1): `types` holds the one type.
 */
import { useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  TICKET_DESCRIPTION_MAX,
  TICKET_TITLE_MAX,
  ticketsApi,
  type Ticket,
  type TicketType,
} from '@/api/tickets';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { TICKET_TYPE_KEYS, ticketErrorCopy } from './ticketCopy';

const TITLE_ID = 'support-new-ticket-title';
const ALL_TYPES: readonly TicketType[] = ['SUPPORT_CASE', 'FEATURE_REQUEST'];

export function NewTicketDialog({
  initialType,
  types = ALL_TYPES,
  onClose,
  onFiled,
}: {
  initialType: TicketType;
  /** The types offered; with one, no choice is shown. */
  types?: readonly TicketType[] | undefined;
  onClose: () => void;
  onFiled: (ticket: Ticket) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [type, setType] = useState<TicketType>(initialType);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement>(null);
  const { dirty } = useDirty({ type, title, description });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);
  const ready = title.trim().length > 0 && description.trim().length > 0;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const ticket = await ticketsApi.create({
        type,
        title: title.trim(),
        description: description.trim(),
      });
      onFiled(ticket);
      onClose();
    } catch (caught) {
      setError(ticketErrorCopy(t, caught, locale));
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      fullWidth
      maxWidth="sm"
      slotProps={{ transition: { onEntered: () => titleRef.current?.focus() } }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>{t('support.tickets.newTitle')}</DialogTitle>
        <DialogContent>
          <Stack sx={{ gap: 2, pt: 0.5 }}>
            {error !== null && <Alert severity="error">{error}</Alert>}
            {types.length > 1 && (
              <FormControl>
                <FormLabel id={`${TITLE_ID}-type`}>{t('support.tickets.typeLabel')}</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby={`${TITLE_ID}-type`}
                  value={type}
                  onChange={(event) => setType(event.target.value as TicketType)}
                >
                  {types.map((value) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio />}
                      label={t(TICKET_TYPE_KEYS[value])}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            )}
            <TextField
              label={t('support.tickets.titleLabel')}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              inputRef={titleRef}
              required
              fullWidth
              slotProps={{ htmlInput: { maxLength: TICKET_TITLE_MAX } }}
            />
            <TextField
              label={t('support.tickets.descriptionLabel')}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              required
              fullWidth
              multiline
              minRows={4}
              maxRows={12}
              slotProps={{ htmlInput: { maxLength: TICKET_DESCRIPTION_MAX } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" disabled={!ready} loading={submitting}>
            {t('support.tickets.file')}
          </SubmitButton>
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}
